import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Archive, Loader2, RefreshCw, AlertTriangle, CheckCircle2, Clock } from 'lucide-react'
import clsx from 'clsx'
import { backupApi, formatBytes, normalizeStatus, summarizeRetention } from '../../services/backupApi'
import { getTenantId } from '../../config/tenant'
import { formatDateTime } from '../../utils/format'

const STALE_HOURS = 36 // sem backup OK há mais que isso = atrasado

function healthOf(row) {
  if (!row.lastSuccessAt) return normalizeStatus(row.lastStatus) === 'Failed' ? 'failed' : 'pending'
  const ageH = (Date.now() - new Date(row.lastSuccessAt.endsWith('Z') ? row.lastSuccessAt : `${row.lastSuccessAt}Z`).getTime()) / 36e5
  if (normalizeStatus(row.lastStatus) === 'Failed') return 'failed'
  return ageH > STALE_HOURS ? 'stale' : 'ok'
}

const HEALTH = {
  ok: { label: 'Em dia', cls: 'text-green-700 bg-green-50', Icon: CheckCircle2 },
  stale: { label: 'Atrasado', cls: 'text-amber-700 bg-amber-50', Icon: Clock },
  failed: { label: 'Falhando', cls: 'text-red-700 bg-red-50', Icon: AlertTriangle },
  pending: { label: 'Aguardando 1º backup', cls: 'text-gray-600 bg-gray-100', Icon: Clock },
}

export default function BackupsPage() {
  const { t } = useTranslation()
  const tenantId = getTenantId()
  const [filter, setFilter] = useState('all')

  const overview = useQuery({
    queryKey: ['backupOverview', tenantId],
    queryFn: () => backupApi.overview(tenantId),
    enabled: !!tenantId,
    refetchInterval: 60000,
  })
  const policies = useQuery({
    queryKey: ['backupPolicies', tenantId],
    queryFn: () => backupApi.listPolicies(tenantId),
    enabled: !!tenantId,
  })

  const rows = useMemo(() => (overview.data || []).map((r) => ({ ...r, health: healthOf(r) })), [overview.data])
  const counts = useMemo(() => rows.reduce((acc, r) => ({ ...acc, [r.health]: (acc[r.health] || 0) + 1 }), {}), [rows])
  const visible = filter === 'all' ? rows : rows.filter((r) => r.health === filter)
  const totalBytes = rows.reduce((s, r) => s + (r.retainedBytes || 0), 0)

  return (
    <div className="p-4 md:p-6 space-y-4 max-w-7xl mx-auto">
      <div className="flex items-center gap-3">
        <Archive className="w-6 h-6 text-theme-secondary" />
        <div className="flex-1">
          <h1 className="text-xl font-semibold text-gray-900">{t('backups.title', 'Backups')}</h1>
          <p className="text-sm text-gray-500">
            {t('backups.subtitle', 'Situação dos backups de cada dispositivo do tenant. Arquivos guardados no object storage (S3, privado).')}
          </p>
        </div>
        <button onClick={() => overview.refetch()} className="btn btn-secondary text-sm" title="Atualizar">
          <RefreshCw className={clsx('w-4 h-4', overview.isFetching && 'animate-spin')} />
        </button>
      </div>

      {/* Políticas */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h2 className="text-sm font-semibold text-gray-900 mb-2">{t('backups.policies', 'Políticas')}</h2>
        {policies.isLoading ? (
          <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
        ) : (policies.data || []).length === 0 ? (
          <p className="text-xs text-gray-400">Nenhuma política. Tenants com MikroTik recebem a política padrão automaticamente (diária, 30 dias / 12 meses / anual).</p>
        ) : (
          <ul className="space-y-1 text-xs text-gray-700">
            {policies.data.map((p) => (
              <li key={p.id}>
                <span className="font-medium">{p.name}</span>
                {p.autoApplyToRouters && <span className="ml-2 px-1.5 py-0.5 rounded bg-theme-primary/10 text-theme-primary">todos os MikroTiks</span>}
                {!p.enabled && <span className="ml-2 text-amber-700">(desabilitada)</span>}
                <span className="ml-2 text-gray-500">{p.timeOfDay} ({p.timeZoneId}) · {summarizeRetention(p)}</span>
              </li>
            ))}
          </ul>
        )}
        <p className="mt-2 text-[11px] text-gray-400">Para editar políticas, abra qualquer dispositivo → aba Backup.</p>
      </div>

      {/* Resumo + filtro */}
      <div className="flex flex-wrap items-center gap-2 text-sm">
        {[['all', `Todos (${rows.length})`], ['ok', `Em dia (${counts.ok || 0})`], ['stale', `Atrasados (${counts.stale || 0})`], ['failed', `Falhando (${counts.failed || 0})`], ['pending', `Aguardando (${counts.pending || 0})`]].map(([key, label]) => (
          <button
            key={key}
            onClick={() => setFilter(key)}
            className={clsx('px-3 py-1 rounded-full border text-xs', filter === key ? 'border-theme-primary text-theme-primary bg-theme-primary/10' : 'border-gray-200 text-gray-600 bg-white')}
          >
            {label}
          </button>
        ))}
        <span className="ml-auto text-xs text-gray-500">Armazenado: {formatBytes(totalBytes)}</span>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
        {overview.isLoading ? (
          <div className="flex items-center justify-center py-12 text-gray-500 text-sm">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Carregando…
          </div>
        ) : overview.isError ? (
          <div className="p-6 text-sm text-red-700">Não foi possível carregar a visão de backups.</div>
        ) : visible.length === 0 ? (
          <div className="p-6 text-sm text-gray-500 text-center">Nenhum dispositivo neste filtro.</div>
        ) : (
          <table className="min-w-full text-xs">
            <thead className="bg-gray-50 text-gray-500 uppercase tracking-wide">
              <tr>
                <th className="text-left font-medium px-3 py-2">Dispositivo</th>
                <th className="text-left font-medium px-3 py-2">Situação</th>
                <th className="text-left font-medium px-3 py-2 whitespace-nowrap">Último backup OK</th>
                <th className="text-left font-medium px-3 py-2">Última tentativa</th>
                <th className="text-right font-medium px-3 py-2">Guardados</th>
                <th className="text-right font-medium px-3 py-2">Tamanho</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {visible.map((r) => {
                const h = HEALTH[r.health]
                return (
                  <tr key={r.managedDeviceId} className="hover:bg-gray-50 align-top">
                    <td className="px-3 py-2">
                      <Link to={`/managed-devices/${r.managedDeviceId}`} className="font-medium text-gray-900 hover:text-theme-primary">
                        {r.deviceName}
                      </Link>
                      <div className="text-gray-400">{r.kind} · {r.deviceStatus}</div>
                    </td>
                    <td className="px-3 py-2">
                      <span className={clsx('inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-medium', h.cls)}>
                        <h.Icon className="w-3 h-3" /> {h.label}
                      </span>
                    </td>
                    <td className="px-3 py-2 font-mono whitespace-nowrap">{r.lastSuccessAt ? formatDateTime(r.lastSuccessAt) : '—'}</td>
                    <td className="px-3 py-2">
                      <div className="font-mono whitespace-nowrap">{r.lastAttemptAt ? formatDateTime(r.lastAttemptAt) : '—'}</div>
                      {r.lastError && <div className="text-red-600 max-w-[50ch] truncate" title={r.lastError}>{r.lastError}</div>}
                    </td>
                    <td className="px-3 py-2 text-right">{r.retainedCount}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">{formatBytes(r.retainedBytes)}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
